import { computed, onMounted, onUnmounted, ref } from 'vue'

// visualViewport.height is never bit-identical to the layout viewport (100dvh):
// scrollbars, URL-bar collapse animations, etc. all shave a few px off it even
// with no keyboard open. Only treat it as "the keyboard opened" past this
// margin — comfortably above that noise, comfortably below the smallest real
// on-screen keyboard (~200px+) — so ordinary loads never leave `100dvh`, and
// the pixel value only kicks in for the actual keyboard-avoidance case that
// needs it.
const KEYBOARD_INSET_THRESHOLD_PX = 100

/**
 * Tracks the visual viewport so a full-screen layout can stay glued to the
 * area that is actually visible above the on-screen keyboard.
 *
 * Why this exists: on iOS Safari / WKWebView the *layout* viewport does NOT
 * shrink when the software keyboard opens — `100vh`, `100svh`, `100lvh` and
 * even `100dvh` all keep reporting the full screen height (dvh only accounts
 * for collapsing browser chrome, not the keyboard). Only the *visual* viewport
 * shrinks, and `position: fixed` stays pinned to the unchanged layout viewport,
 * which is what makes a fixed input row end up hidden behind the keyboard.
 *
 * Binding the chat container's height to `visualViewport.height` (and offsetting
 * it by `visualViewport.offsetTop`, since iOS scrolls the layout viewport when
 * the keyboard appears) keeps the input above the keyboard on every platform.
 *
 * Falls back to `100dvh` before mount, when the API is unavailable (SSR, older
 * engines), and whenever the keyboard isn't actually eating space — so the
 * common case never trades a stable, native `dvh` for a JS-measured pixel
 * value that's merely close to it, which is what caused the input bar to
 * visibly shift on every load, keyboard or not.
 */
export function useVisualViewport() {
  // Height of the area not covered by the keyboard, or null when the keyboard
  // isn't open (including before mount / when the VisualViewport API is
  // unavailable), in which case the caller should fall back to `100dvh`.
  const height = ref<number | null>(null)
  const offsetTop = ref(0)

  let viewport: VisualViewport | null = null
  let rafId: number | null = null

  function sync() {
    if (!viewport) return
    const keyboardInset = window.innerHeight - viewport.height
    height.value =
      keyboardInset > KEYBOARD_INSET_THRESHOLD_PX ? viewport.height : null
    offsetTop.value = viewport.offsetTop
  }

  function scheduleSync() {
    if (rafId !== null) return
    rafId = window.requestAnimationFrame(() => {
      rafId = null
      sync()
    })
  }

  onMounted(() => {
    viewport = window.visualViewport ?? null
    if (!viewport) return
    sync()
    viewport.addEventListener('resize', scheduleSync)
    viewport.addEventListener('scroll', scheduleSync)
  })

  onUnmounted(() => {
    if (rafId !== null) window.cancelAnimationFrame(rafId)
    if (!viewport) return
    viewport.removeEventListener('resize', scheduleSync)
    viewport.removeEventListener('scroll', scheduleSync)
  })

  // Style for the chat root: a definite pixel height keeps the inner flex
  // column (and its scroll area) sized to the visible region, while the
  // translate compensates for iOS shifting the layout viewport up.
  const style = computed(() => ({
    height: height.value === null ? '100dvh' : `${height.value}px`,
    transform: `translateY(${offsetTop.value}px)`,
  }))

  return { height, offsetTop, style }
}
